import { AttributesMixin, Model, type } from "@type-r/models";
import { AssetData, State } from "./assetData";
import { AssetEvent, AssetEventStatus } from "./assetEvent";
import { ExternalSystem } from "./externalSystemRequest";
import { remove } from 'lodash';
export abstract class AssetChange extends Model {

    static attributes = {
        assetId: String,
        changes: Object,
        errors: type(Object).dontSave,
        warnings: type(Object).dontSave,
        validationCategory: Number,
        asset: type(Object),
        createdBy: String,
        createdOn: Date,
        assetEvents: type([AssetEvent]).dontSave,
        isArchived: type(Boolean).null
    };

    abstract getAssetType();
    abstract getAssetName();

    getValue(propName: string) {
        const dynamic: any = this;
        if (dynamic.changes && dynamic.changes[propName]) return dynamic.changes[propName];
        if (dynamic.updateAsset) return dynamic.updateAsset[propName];
        return null;
    }

    setValue(propName: string, value) {
        const dynamic: any = this;
        dynamic.changes[propName] = value;
    }

    syncFrom(asset: any) {
        const dynamic: any = this;

        dynamic['asset'] = asset;
        dynamic.changes = asset.toJSON();
        delete dynamic.changes['validationCategory'];
        delete dynamic.changes['errors'];
        delete dynamic.changes['warnings'];
        delete dynamic['errors'];
        delete dynamic['warnings'];
        //TODO: need to find out the purpose of this code fragment
        // if (dynamic.updateAsset) {
        //     asset.forEach((value, key) => {
        //         if (value === dynamic.updateAsset[ key ]) {
        //             delete dynamic.changes[ key ];
        //         }
        //     })
        // }
    }

    syncTo(asset: AssetData & AttributesMixin<typeof AssetData>) {
        const dynamic: any = this;

        asset.set(asset.defaults());
        if (dynamic.updateAsset) {
            asset.assignFrom(dynamic.updateAsset);
        }
        asset.set(dynamic.changes);

        asset.errors = dynamic.errors;
        asset.warnings = dynamic.warnings;
        asset.assetId = dynamic.id;

        asset.onLoaded();
    }

    updateWithInfoFromApi(asset: AssetData & AttributesMixin<typeof AssetData>){
        const dynamic: any = this;

        //ODIN-1428: asset_facilitator user can type in supplier/buyer names without dropdown
        //if the typed in name is a valid participant_alias, the api will return platform_company name
        //this platform_company name needs to be updated in the grid/edit form.
        if(dynamic.updateAsset && dynamic.updateAsset.supplier && dynamic.updateAsset.supplier.name){
            asset["supplier"] =  dynamic.updateAsset.supplier;    //edit form
            dynamic.changes["supplier"] = dynamic.updateAsset.supplier; //grid
        }
        if(dynamic.updateAsset && dynamic.updateAsset.buyer && dynamic.updateAsset.buyer.name){
            asset["buyer"] = dynamic.updateAsset.buyer;                 //edit form
            dynamic.changes["buyer"] = dynamic.updateAsset.buyer;       //grid
        }

        asset.onLoaded();
    }

    get state(): State {
        var result = new State();
        const state = this.getValue("state");
        if (state) {
            result.assignFrom(state);
        }
        return result;
    }

    get isPending(): boolean {
        return this.state && this.state.name === 'Pending';
    }

    get isDigitized(): boolean {
        return this.state && this.state.name === 'Digitized';
    }

    set state(value: State) {
        this.setValue("state", value);
    }

    resetAssetEvents(eventTypeId, incomingAssetEvents) {
        const dynamic: any = this;

        if (dynamic.assetEvents.length) {
            dynamic.assetEvents.forEach((item: AssetEvent, index: number) => {

                if (Boolean(item) && item.eventTypeId == eventTypeId && !item.success) {
                    const incomingAssetEventIndex = incomingAssetEvents.models.findIndex(ae => ae.id == item.id);

                    if (incomingAssetEventIndex !== -1) {
                        incomingAssetEvents.models.splice(incomingAssetEventIndex, 1);
                    }

                    dynamic.assetEvents.models.splice(index, 1);
                }
            });
        }
    }

    requireAssetEvent(eventTypeId: number, isSend: boolean): AssetEvent {
        const dynamic: any = this;

        remove(dynamic.assetEvents.models, (item: any) => {
            return item.eventTypeId == eventTypeId && item.status == 0;
        });
        const existingEvent = dynamic.assetEvents.filter(ae => ae.eventTypeId == eventTypeId);

        if (existingEvent.length > 0 && !isSend) {
            existingEvent[0].status = 0;
            return existingEvent[0];
        }
        // if (existingEvent.length > 0 && !isSend) {
        //     if(existingEvent.length > 1){
        //         existingEvent.filter(x => x.status === 0)
        //         existingEvent[0].status = 0;
        //         dynamic.assetEvents;
        //     }
        //     return existingEvent[0];
        // }

        dynamic.assetEvents.add({ eventTypeId, status: AssetEventStatus.pending });
        return dynamic.assetEvents.filter(ae => ae.eventTypeId == eventTypeId)[0];
    }

    hasReceivedAssetEventsFor(filterEventType: { eventTypeId: number, success: boolean }): boolean {
        const dynamic: any = this;
        if (filterEventType) {
            return dynamic.assetEvents.filter(ae =>
                ae.success == filterEventType.success && ae.eventTypeId == filterEventType.eventTypeId).length > 0;
        }
        return true;
    }

    filter(value: string) {
        return true;
    }
}

export const syncAssetEventsToAssets = (assetChanges, incomingAssetEvents, externalSystems: ExternalSystem[], $refreshEvents?) => {
    let assetEventDict = {}
    const eventsToProcess = incomingAssetEvents.models || incomingAssetEvents;
    eventsToProcess.forEach(assetEvent => {
        var assetId = assetEvent.uid;
        if (!assetEventDict[assetId])
            assetEventDict[assetId] = []
        assetEventDict[assetId].push(assetEvent)
    });

    assetChanges.transaction(() => {
        assetChanges.forEach(assetChange => {
            let incomingAssetEventsForAssetId = assetEventDict[assetChange.assetId];
            if (!incomingAssetEventsForAssetId)
                return
            incomingAssetEventsForAssetId.forEach(incomingAssetEvent => {
                if (incomingAssetEvent.uid != assetChange.assetId ||
                    externalSystems.filter(es => es.assetEventType === incomingAssetEvent.eventTypeId).length <= 0) return;

                const assetEvent = assetChange.requireAssetEvent(incomingAssetEvent.eventTypeId);

                if (!assetEvent.id || assetEvent.id != incomingAssetEvent.id || assetEvent.status == AssetEventStatus.pending) {
                    assetEvent.set({ ...incomingAssetEvent.attributes, status: AssetEventStatus.received });
                    if ($refreshEvents) {
                        $refreshEvents.set(!$refreshEvents.value);
                    }
                }
            });
        });
    });
};
